import { memo, useCallback } from 'react';

import { SimLink } from '@/components/context-graph-viewer/helpers/graph-types';

interface GraphLinkProps {
  link: SimLink;
  visible: boolean;
  active: boolean;
  /** Pre-combined `active && hasFocus` so a focus flip only re-renders links whose styling changes. */
  highlighted: boolean;
  onHover: (id: string | null) => void;
  onSelect: (link: SimLink) => void;
  registerElement: (id: string, element: SVGGElement | null) => void;
}

// Memoized so hover/selection/search/timeline changes only re-render the
// links whose props actually changed. Positions are written imperatively via
// registerElement (updatePositions), never through props. Keep the rendered
// DOM structure identical to what export-image expects (it pairs elements by
// querySelectorAll index).
export const GraphLink = memo(function GraphLink({
  link,
  visible,
  active,
  highlighted,
  onHover,
  onSelect,
  registerElement,
}: GraphLinkProps) {
  const setRef = useCallback(
    (element: SVGGElement | null) => registerElement(link.id, element),
    [registerElement, link.id],
  );

  return (
    <g
      ref={setRef}
      className='transition-opacity duration-200'
      style={{
        opacity: visible ? (active ? 1 : 0.08) : 0,
        pointerEvents: visible ? undefined : 'none',
      }}
      onPointerEnter={() => onHover(link.id)}
      onPointerLeave={() => onHover(null)}
      onPointerDown={(event) => {
        event.stopPropagation();
        onSelect(link);
      }}
    >
      <path
        fill='none'
        stroke={highlighted ? 'var(--primary)' : 'var(--muted-foreground)'}
        strokeOpacity={highlighted ? 0.85 : 0.3}
        strokeWidth={highlighted ? 1.6 : 1}
      />
      <path
        fill='none'
        stroke='transparent'
        strokeWidth={11}
        className='cursor-pointer'
      />
    </g>
  );
});
